/**
 * Audio Context Polyfill
 *
 * Since Chrome 55 and the removal of the webkitAudioContext, this is a polyfill to support the new
 * Web Audio API through multiple browsers. What it does is if a browser does not have access to the
 * new API, it creates methods with the new names and any additional functionality for the framework
 * to work.
 *
 * The old API will in this case still work, but the new API as well, so any products using the
 * framework with old API will still work.
 *
 * Because of all this, the recommended usage is to only use the new API.
 *
 * See MDN for more information:
 * https://developer.mozilla.org/en-US/docs/Web/API/Web_Audio_API/Porting_webkitAudioContext_code_to_standards_based_AudioContext
 *
 * Link to AudioContext API:
 * https://developer.mozilla.org/en/docs/Web/API/AudioContext
 */

/* eslint-disable wrap-iife */
(function(globals) {
    "use strict";

    var WebkitAudioContext = globals.webkitAudioContext,
        fixSetTarget = function(param) {
            if (!param) {
                return;
            }
            if (!param.setTargetAtTime) {
                param.setTargetAtTime = param.setTargetValueAtTime;
            }
        };

    // If new context does not exist, but this is a webkit browser, start the polyfill process
    if (!globals.AudioContext && WebkitAudioContext) {
        window.AudioContext = WebkitAudioContext;

        // These checks are for Safari, since in some versions the new API is used, but still within
        // the old context. Therefor we have to explicitly check whether the new API is supported or not
        // per requested function.
        if (!WebkitAudioContext.prototype.createGain) {
            AudioContext.prototype._createGain = WebkitAudioContext.prototype.createGainNode;
            AudioContext.prototype.createGain = function() {
                var node = this._createGain();

                fixSetTarget(node.gain);
                return node;
            };
        }

        if (!WebkitAudioContext.prototype.createDelay) {
            AudioContext.prototype._createDelay = WebkitAudioContext.prototype.createDelayNode;
            AudioContext.prototype.createDelay = function() {
                var node = this._createDelay();

                fixSetTarget(node.delayTime);
                return node;
            };
        }

        AudioContext.prototype.createScriptProcessor = WebkitAudioContext.prototype.createScriptProcessor || WebkitAudioContext.prototype.createJavascriptNode;

        AudioContext.prototype._createOscillator = WebkitAudioContext.prototype.createOscillator;
        AudioContext.prototype.createOscillator = function() {
            var osc = this._createOscillator();

            if (!osc.start) {
                osc.start = osc.noteOn;
            }
            if (!osc.stop) {
                osc.stop = osc.noteOff;
            }

            return osc;
        };

        AudioContext.prototype._createBufferSource = WebkitAudioContext.prototype.createBufferSource;
        AudioContext.prototype.createBufferSource = function() {
            var src = this._createBufferSource();

            if (!src.start) {
                src.start = src.noteGrainOn || src.noteOn;
            }
            if (!src.stop) {
                src.stop = src.noteOff;
            }
            fixSetTarget(src.playbackRate);
            return src;
        };
    }
})(window);
